import { Check } from 'lucide-react';
import { CoinBurst } from '@/components/coins';
import type {
  AnswerResult,
  AttemptCompletionResult,
  QuizQuestionView,
  QuizView,
} from '@/types/api';

/**
 * QuizScore — final screen. Coin-centric success view.
 *
 * Structure:
 *   - Coin burst (only when this attempt earned coins)
 *   - Score subtitle ("N out of N correct") + optional "try again" nudge
 *   - "Let's review" heading + one row per question
 *   - Footer actions: Re-read, Try again, and a contextual primary action —
 *     "Back to Library" for a book quiz, "More articles" for an article quiz.
 *
 * The old medallion + "creature collected" card were retired along with
 * the badge system; coins are the sole reward signal now.
 */
export interface QuizScoreProps {
  quiz: QuizView;
  completion: AttemptCompletionResult;
  picks: Record<string, string>;
  results: Record<string, AnswerResult>;
  onTryAgain: () => void;
  onBackToParent: () => void;
  onMore: () => void;
}

export function QuizScore({
  quiz,
  completion,
  picks,
  results,
  onTryAgain,
  onBackToParent,
  onMore,
}: QuizScoreProps) {
  const passed = completion.passed;
  // Passed, but this completion granted no coins while the quiz *does*
  // carry a reward — the only way that happens is the reader already
  // earned the coins on an earlier attempt (coins are once-per-quiz).
  const alreadyRewarded = passed && completion.coinsEarned === 0 && quiz.coinReward > 0;

  return (
    <section className="flex flex-col items-center text-center">
      {/* Coin reward burst — fires when this attempt earned coins for
          the first time. `coinsEarned` is 0 on replays, sub-threshold
          attempts, and retakes of an already-earned quiz, so the burst
          naturally hides itself in those cases. */}
      {completion.coinsEarned > 0 ? (
        <div className="mb-4">
          <CoinBurst amount={completion.coinsEarned} variant="reward-burst" />
        </div>
      ) : null}

      {/* Score subtitle — the primary success/failure signal now that
          the medallion is gone. */}
      <p
        style={{
          fontFamily: 'Inter, sans-serif',
          fontSize: '16px',
          color: '#8A7F72',
          margin: 0,
        }}
      >
        {completion.correctCount} out of {completion.totalCount} correct
      </p>
      {!passed ? (
        <p
          style={{
            fontFamily: 'Inter, sans-serif',
            fontSize: '14px',
            color: '#8A7F72',
            margin: '6px 0 0',
            maxWidth: '420px',
          }}
        >
          So close! Give it another try to earn coins.
        </p>
      ) : alreadyRewarded ? (
        <p
          style={{
            fontFamily: 'Inter, sans-serif',
            fontSize: '14px',
            color: '#8A7F72',
            margin: '6px 0 0',
            maxWidth: '420px',
          }}
        >
          You already earned your coins for this quiz on an earlier attempt, so no extra coins this
          time — but nice refresher!
        </p>
      ) : null}

      {/* Review */}
      <div className="mt-10 w-full text-left">
        <h2
          style={{
            fontFamily: '"DM Serif Display", serif',
            fontWeight: 400,
            fontSize: '24px',
            color: '#1F1B16',
            margin: '0 0 12px',
          }}
        >
          Let's review
        </h2>
        <div className="flex flex-col" style={{ gap: '14px' }}>
          {quiz.questions.map((q, i) => (
            <ReviewRow
              key={q.id}
              index={i}
              question={q}
              picked={picks[q.id] ?? null}
              result={results[q.id] ?? null}
            />
          ))}
        </div>
      </div>

      {/* Footer actions */}
      <div
        className="mt-10 flex w-full flex-col gap-2 sm:flex-row sm:justify-center"
        style={{ gap: '12px' }}
      >
        <SecondaryButton onClick={onBackToParent}>Re-read</SecondaryButton>
        <SecondaryButton onClick={onTryAgain}>Try again</SecondaryButton>
        <PrimaryButton onClick={onMore}>
          {quiz.bookId ? 'Back to Library' : 'More articles'}
        </PrimaryButton>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------

interface ReviewRowProps {
  index: number;
  question: QuizQuestionView;
  picked: string | null;
  result: AnswerResult | null;
}

function ReviewRow({ index, question, picked, result }: ReviewRowProps) {
  const correctOption = question.options.find((o) => o.id === question.correctOptionId);
  const pickedOption = picked ? question.options.find((o) => o.id === picked) : null;
  const isCorrect = result?.isCorrect ?? null;
  const didNotAnswer = !pickedOption;

  return (
    <div
      className="flex gap-3"
      style={{
        backgroundColor: '#FFFFFF',
        border: '1px solid #EFE5D2',
        borderRadius: '14px',
        padding: '14px 16px',
      }}
    >
      <div
        style={{
          width: '36px',
          height: '36px',
          borderRadius: '999px',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: isCorrect === null ? '#EFE5D2' : isCorrect ? '#4CAF50' : '#D94F3A',
          color: '#FFFFFF',
          flexShrink: 0,
        }}
        aria-label={isCorrect === null ? 'Not answered' : isCorrect ? 'Correct' : 'Incorrect'}
      >
        {isCorrect === null ? (
          <span style={{ color: '#8A7F72' }}>—</span>
        ) : isCorrect ? (
          <Check className="h-4 w-4" aria-hidden strokeWidth={3} />
        ) : (
          <span aria-hidden>✕</span>
        )}
      </div>
      <div className="flex-1">
        <div
          style={{
            fontFamily: 'Inter, sans-serif',
            fontSize: '11px',
            fontWeight: 600,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: '#8A7F72',
          }}
        >
          Question {index + 1}
        </div>
        <div
          style={{
            fontFamily: '"DM Serif Display", serif',
            fontSize: '18px',
            color: '#1F1B16',
            lineHeight: 1.25,
            marginTop: '2px',
          }}
        >
          {question.prompt}
        </div>
        <div
          style={{
            fontFamily: 'Inter, sans-serif',
            fontSize: '14px',
            color: '#1F1B16',
            marginTop: '8px',
          }}
        >
          {didNotAnswer ? (
            <span style={{ color: '#8A7F72' }}>You didn't answer this one.</span>
          ) : (
            <>
              You answered: <span style={{ color: '#1F1B16' }}>{pickedOption.text}</span>
              {isCorrect === false && correctOption ? (
                <span style={{ color: '#8A7F72' }}> (correct: {correctOption.text})</span>
              ) : null}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

function PrimaryButton({ onClick, children }: { onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center justify-center transition-colors"
      style={{
        backgroundColor: '#2E8A8A',
        color: '#FFFFFF',
        padding: '12px 22px',
        borderRadius: '999px',
        fontFamily: 'Inter, sans-serif',
        fontSize: '14px',
        fontWeight: 600,
        border: 'none',
        cursor: 'pointer',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = '#1F6A6A';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = '#2E8A8A';
      }}
    >
      {children}
    </button>
  );
}

function SecondaryButton({
  onClick,
  children,
}: {
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center justify-center"
      style={{
        backgroundColor: '#FFFFFF',
        color: '#1F6A6A',
        padding: '12px 22px',
        borderRadius: '999px',
        fontFamily: 'Inter, sans-serif',
        fontSize: '14px',
        fontWeight: 600,
        border: '1px solid #E3F1EF',
        cursor: 'pointer',
      }}
    >
      {children}
    </button>
  );
}
